"use client";

import Link from "next/link";
import { ArrowLeft, Printer } from "lucide-react";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { usePrefs } from "@/components/prefs-provider";
import { LEGAL_ENTITY, legalDocs, type LegalBlock } from "@/lib/legal-content";
import { Button } from "./ui/button";
import { Notice } from "./ui/feedback";

function Block({ block }: { block: LegalBlock }) {
  if (typeof block === "string") return <p>{block}</p>;
  if ("list" in block) {
    return (
      <ul>
        {block.list.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    );
  }
  if ("ol" in block) {
    return (
      <ol>
        {block.ol.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ol>
    );
  }
  return (
    <Notice tone="neutral" compact className="my-4" live="off">
      {block.note}
    </Notice>
  );
}

export function LegalDocument({ kind }: { kind: keyof typeof legalDocs }) {
  const { t, tf, locale } = usePrefs();
  const doc = legalDocs[kind][locale] ?? legalDocs[kind].he;
  const otherDocs = (Object.keys(legalDocs) as (keyof typeof legalDocs)[]).filter((k) => k !== kind);
  const labels: Record<keyof typeof legalDocs, string> = {
    terms: t.legal.terms,
    privacy: t.legal.privacy,
    accessibility: t.legal.accessibility,
  };
  const href: Record<keyof typeof legalDocs, string> = {
    terms: "/terms",
    privacy: "/privacy",
    accessibility: "/accessibility",
  };

  return (
    <div className="min-h-screen bg-bg">
      <SiteHeader solid />
      <main id="main-content" className="mx-auto max-w-6xl px-4 pb-20 pt-28 sm:px-6">
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_16rem] lg:gap-12">
          <article className="legal-doc min-w-0 max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-brand">{LEGAL_ENTITY.brand}</p>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">{doc.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-ink-2">
              <span>{tf(t.legal.updated, { date: LEGAL_ENTITY.updated })}</span>
              <Button variant="outline" size="sm" className="no-print" onClick={() => window.print()} icon={<Printer className="h-3.5 w-3.5" aria-hidden />}>
                {t.legal.print}
              </Button>
            </div>

            <p className="mt-6 text-base leading-relaxed text-ink">{doc.intro}</p>

            {/* Mobile TOC */}
            <nav aria-label={t.legal.toc} className="no-print mt-8 rounded-2xl bg-surface-2 p-5 lg:hidden">
              <p className="text-xs font-bold uppercase tracking-wide text-ink-2">{t.legal.toc}</p>
              <ol className="mt-2 grid gap-1.5 text-sm sm:grid-cols-2" style={{ listStyle: "none", paddingInlineStart: 0 }}>
                {doc.sections.map((s) => (
                  <li key={s.id} style={{ marginTop: 0 }}>
                    <a href={`#${s.id}`} className="text-brand underline-offset-2 hover:underline">
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {doc.sections.map((s) => (
              <section key={s.id} id={s.id} aria-labelledby={`${s.id}-h`} className="scroll-mt-24">
                <h2 id={`${s.id}-h`}>{s.heading}</h2>
                {s.blocks.map((b, i) => (
                  <Block key={i} block={b} />
                ))}
              </section>
            ))}

            <div className="no-print mt-12 flex flex-wrap items-center gap-4 border-t border-line pt-6 text-sm">
              <Link href="/" className="inline-flex items-center gap-1.5 font-semibold text-brand underline-offset-2 hover:underline">
                <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" aria-hidden />
                {t.legal.back}
              </Link>
              {otherDocs.map((k) => (
                <Link key={k} href={href[k]} className="text-ink-2 underline-offset-2 hover:text-ink hover:underline">
                  {labels[k]}
                </Link>
              ))}
            </div>
          </article>

          {/* Desktop sticky TOC */}
          <aside className="no-print hidden lg:block">
            <nav aria-label={t.legal.toc} className="sticky top-24">
              <p className="text-xs font-bold uppercase tracking-wide text-ink-2">{t.legal.toc}</p>
              <ol className="mt-3 space-y-1 border-s border-line text-sm">
                {doc.sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="-ms-px block border-s-2 border-transparent ps-3 py-1 leading-snug text-ink-2 transition-colors hover:border-brand hover:text-ink"
                    >
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ol>
              <div className="mt-6 space-y-1 text-sm">
                {otherDocs.map((k) => (
                  <Link key={k} href={href[k]} className="block text-ink-2 hover:text-ink">
                    {labels[k]}
                  </Link>
                ))}
              </div>
            </nav>
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
